/**
 * Screen Reader Utilities
 * 
 * Utilities for announcing messages to screen readers
 * Requirements: 13.2, 13.5
 */

/**
 * Announce a message to screen readers using ARIA live regions
 * @param message - The message to announce
 * @param priority - The priority level ('polite' or 'assertive')
 */
export function announceToScreenReader(
  message: string,
  priority: 'polite' | 'assertive' = 'polite'
): void {
  // Find or create the live region
  let liveRegion = document.getElementById(`aria-live-${priority}`);
  
  if (!liveRegion) {
    liveRegion = document.createElement('div');
    liveRegion.id = `aria-live-${priority}`;
    liveRegion.setAttribute('aria-live', priority);
    liveRegion.setAttribute('aria-atomic', 'true');
    liveRegion.setAttribute('role', 'status');
    liveRegion.className = 'sr-only'; // Visually hidden but accessible to screen readers
    document.body.appendChild(liveRegion);
  }

  // Clear previous message
  liveRegion.textContent = '';

  // Set new message after a brief delay to ensure screen readers pick it up
  setTimeout(() => {
    liveRegion!.textContent = message;
  }, 100);

  // Clear the message after it's been announced
  setTimeout(() => {
    liveRegion!.textContent = '';
  }, 3000);
}

/**
 * Announce an error to screen readers
 * Uses assertive priority to interrupt current announcements
 */
export function announceError(message: string): void {
  announceToScreenReader(`Error: ${message}`, 'assertive');
}

/**
 * Announce a success message to screen readers
 */
export function announceSuccess(message: string): void {
  announceToScreenReader(`Success: ${message}`, 'polite');
}

/**
 * Announce a loading state to screen readers
 */
export function announceLoading(message: string = 'Loading...'): void {
  announceToScreenReader(message, 'polite');
}

/**
 * Announce navigation to screen readers
 */
export function announceNavigation(pageName: string): void {
  announceToScreenReader(`Navigated to ${pageName}`, 'polite');
}
